import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import { ToastService } from '@storybook/services/toast/toast.service';
import { filter, finalize, forkJoin, switchMap } from 'rxjs';
import { MaxxAlertMetric, MaxxMonitor, MaxxSlackChannel } from '../maxx-settings.model';
import { MaxxSettingsService, maxxSettingsErrorMessage } from '../maxx-settings.service';

interface MonitorRow {
  metric: MaxxAlertMetric;
  title: string;
  subtitle: string;
  statusLabel: string;
  statusVariant: 'success' | 'secondary' | 'tertiary';
  monitor: MaxxMonitor | null;
}

const METRICS: { metric: MaxxAlertMetric; title: string }[] = [
  { metric: 'cvr', title: 'Checkout conversion rate (CVR)' },
  { metric: 'aov', title: 'Average order value (AOV)' },
  { metric: 'arpc', title: 'Average revenue per checkout (ARPC)' },
  { metric: 'asr', title: 'Average shipping revenue (ASR)' },
];

const WEEKDAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

function ordinal(day: number): string {
  const lastTwo = day % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${day}th`;
  return `${day}${{ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th'}`;
}

function scheduleLabel(monitor: MaxxMonitor): string {
  if (monitor.frequency === 'weekly') return `${WEEKDAYS[monitor.dayOfWeek ?? 1]} ${monitor.timeOfDay}`;
  if (monitor.frequency === 'monthly') return `${ordinal(monitor.dayOfMonth ?? 1)} of the month`;
  return 'Daily';
}

@Component({
  selector: 'maxx-monitoring-settings',
  templateUrl: './maxx-monitoring-settings.component.html',
  styleUrls: ['./maxx-monitoring-settings.component.scss'],
  standalone: false,
})
export class MaxxMonitoringSettingsComponent implements OnInit {
  readonly isLoading = signal(true);
  readonly loadFailed = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly editing = signal<MonitorRow | null>(null);
  readonly rows = computed(() => this.buildRows(this.monitors(), this.slackChannel()));
  readonly slackChannelName = computed(() => this.slackChannel()?.name ?? null);

  private readonly monitors = signal<MaxxMonitor[]>([]);
  private readonly slackChannel = signal<MaxxSlackChannel | null>(null);
  private readonly maxxSettingsService = inject(MaxxSettingsService);
  private readonly toastService = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  ngOnInit(): void {
    forkJoin({
      monitors: this.maxxSettingsService.getMonitors(),
      channel: this.maxxSettingsService.getSlackChannel(),
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ monitors, channel }) => {
          this.monitors.set(monitors);
          this.slackChannel.set(channel);
          this.openRequestedMonitor();
        },
        error: (error) => {
          this.loadFailed.set(true);
          this.errorMessage.set(maxxSettingsErrorMessage(error));
        },
      });
  }

  onSaved(saved: MaxxMonitor): void {
    this.monitors.update((monitors) => [...monitors.filter((item) => item.id !== saved.id), saved]);
    this.editing.set(null);
    this.toastService.showToast({ mode: 'info', message: 'Alert saved' });
  }

  setEnabled(monitor: MaxxMonitor, enabled: boolean): void {
    if (monitor.enabled === enabled) return;
    this.maxxSettingsService
      .updateMonitor(monitor.id, { enabled })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.monitors.update((monitors) => monitors.map((item) => (item.id === updated.id ? updated : item)));
          this.toastService.showToast({ mode: 'info', message: updated.enabled ? 'Alert resumed' : 'Alert paused' });
        },
        error: (error) => this.toastService.showToast({ mode: 'danger', message: maxxSettingsErrorMessage(error) }),
      });
  }

  delete(monitor: MaxxMonitor): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        backdropClass: 'global_modal_overlay_subdued',
        data: {
          title: 'Delete alert',
          description: 'MAXX will stop checking this metric. You can create the alert again later.',
          confirmText: 'Delete',
          cancelText: 'Cancel',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.maxxSettingsService.deleteMonitor(monitor.id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.monitors.update((monitors) => monitors.filter((item) => item.id !== monitor.id));
          this.toastService.showToast({ mode: 'info', message: 'Alert deleted' });
        },
        error: (error) => this.toastService.showToast({ mode: 'danger', message: maxxSettingsErrorMessage(error) }),
      });
  }

  private openRequestedMonitor(): void {
    const id = this.route.snapshot.queryParamMap.get('edit');
    if (!id) return;
    const row = this.rows().find((item) => item.monitor?.id === id);
    if (row) this.editing.set(row);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: null, edit: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private buildRows(monitors: MaxxMonitor[], channel: MaxxSlackChannel | null): MonitorRow[] {
    return METRICS.map(({ metric, title }) => {
      const monitor = monitors.find((item) => item.metric === metric) ?? null;
      if (!monitor) {
        return { metric, title, subtitle: 'Not set up', statusLabel: 'Inactive', statusVariant: 'secondary', monitor };
      }
      const delivery = [
        scheduleLabel(monitor),
        channel && monitor.postToSlack !== false ? `posts to #${channel.name}` : null,
        monitor.recipients.length ? `emails ${monitor.recipients.join(', ')}` : null,
      ].filter(Boolean);
      return {
        metric,
        title,
        subtitle: delivery.join(', '),
        statusLabel: monitor.enabled ? 'Active' : 'Paused',
        statusVariant: monitor.enabled ? 'success' : 'tertiary',
        monitor,
      };
    });
  }
}
